import React, { createContext, useContext, useEffect, useState } from "react";
import api from "../api/axios";

/* ====== Cấu hình các key chuẩn dùng trong localStorage ====== */
const LS_KEYS = {
  accountId: "accountId",
  role: "role",
  name: "name",
  token: "token",
  userId: "userId",
  uid: "uid",
  email: "email",
  fullName: "fullName",
};

const ALARM_PREFIX = "alarms:";

/* ====== Clear localStorage nhưng giữ alarms:* ====== */
function safeLocalClear() {
  try {
    const snap: Record<string, string> = {};

    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (k && k.startsWith(ALARM_PREFIX)) {
        const v = localStorage.getItem(k);
        if (v !== null) snap[k] = v;
      }
    }

    sessionStorage.setItem("__alarms_snapshot__", JSON.stringify(snap));
    localStorage.clear();

    const raw = sessionStorage.getItem("__alarms_snapshot__");
    if (raw) {
      const obj = JSON.parse(raw) as Record<string, string>;
      Object.entries(obj).forEach(([k, v]) => localStorage.setItem(k, v));
    }

    sessionStorage.removeItem("__alarms_snapshot__");
  } catch {
    // ignore
  }
}

type AuthUser = {
  accountId: string;
  role: string;
  name?: string;
};

type AuthContextType = {
  user: AuthUser | null;
  login: (user: AuthUser) => void;
  logout: (redirectTo?: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<AuthUser | null>(null);

  useEffect(() => {
    const accountId = localStorage.getItem(LS_KEYS.accountId);
    const role = localStorage.getItem(LS_KEYS.role);
    const name = localStorage.getItem(LS_KEYS.name);

    if (accountId && role) {
      setUser({ accountId, role, name: name ?? undefined });

      if (!name) {
        void hydrateName(accountId);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hydrateName = async (accountId: string) => {
    try {
      const res = await api.get("/api/employee/info", {
        params: { accountId },
      });

      const name =
        res.data?.fullName ??
        res.data?.name ??
        res.data?.displayName ??
        "";

      if (name) {
        setUser((u) => (u ? { ...u, name } : u));
        localStorage.setItem(LS_KEYS.name, name);
      }
    } catch {
      // ignore
    }
  };

  const login = (payload: AuthUser) => {
    setUser(payload);

    localStorage.setItem(LS_KEYS.accountId, payload.accountId);
    localStorage.setItem(LS_KEYS.role, payload.role);

    if (payload.name) {
      localStorage.setItem(LS_KEYS.name, payload.name);
    }

    if (!payload.name) {
      void hydrateName(payload.accountId);
    }
  };

const logout = async (redirectTo?: string) => {
  const accountId = localStorage.getItem(LS_KEYS.accountId);

  try {
    if (accountId) {
      await api.post("/api/auth/logout", { accountId });
    }
  } catch {
    // bỏ qua lỗi logout api
  } finally {
    localStorage.removeItem("accountId");
    localStorage.removeItem("role");
    localStorage.removeItem("name");
    localStorage.removeItem("token");
    localStorage.removeItem("userId");
    localStorage.removeItem("uid");
    localStorage.removeItem("email");
    localStorage.removeItem("fullName");

    // QUAN TRỌNG:
    // Nếu có redirectTo thì đi luôn, KHÔNG setUser(null) trước.
    // Nếu setUser(null), RequireRole sẽ đá về /login.
    if (redirectTo) {
      window.location.replace(redirectTo);
      return;
    }

    setUser(null);
  }
};
  return (
    <AuthContext.Provider value={{ user, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);

  if (!ctx) {
    throw new Error("useAuth must be used within AuthProvider");
  }

  return ctx;
};